import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { setHotelPassengers } from "shared/data/actions/search";
import { RoomPassengers } from "shared/types/hotels";

import { calculateTotalTravelers } from "../../web/lib/Hotels/calculateTotalTravelers";

type MaxTravelersErrorId = "PropertiesSelect.maxPassengersError";
type NeedMoreAdultsErrorId = "PropertiesSelect.needMoreAdultsThanChildren";

interface UseHotelRoomManagementProps {
  searchHotelsRooms: {
    [key: number]: { ADT: number; CHD: number; childAges: number[] };
  };
  hotelMaxRooms?: number | string;
  hotelSearchSettings?: {
    RoomGuestLimits: {
      GuestLimit: Array<{
        Code: string;
        MaxAge?: number;
        LessOrEqualToAdults?: string;
      }>;
    };
  };
}

export const useHotelRoomManagement = ({
  searchHotelsRooms,
  hotelMaxRooms,
  hotelSearchSettings,
  setErrorMessage,
}: UseHotelRoomManagementProps) => {
  const dispatch = useDispatch();

  const transformReduxStateToComponentState = useCallback(
    (reduxRooms: typeof searchHotelsRooms = {}): RoomPassengers[] => {
      const transformed = Object.entries(reduxRooms)
        .sort(([keyA], [keyB]) => parseInt(keyA, 10) - parseInt(keyB, 10))
        .map(([, roomData]) => {
          const childAges = Array.isArray(roomData.childAges)
            ? [...roomData.childAges]
            : [];
          return {
            ADT: roomData.ADT || 0,
            CHD: roomData.CHD || 0,
            childAges,
          };
        });
      if (transformed.length === 0) {
        return [{ ADT: 1, CHD: 0, childAges: [] }];
      }
      return transformed;
    },
    []
  );

  const initialRoomsFromRedux = useMemo(
    () => transformReduxStateToComponentState(searchHotelsRooms),
    [searchHotelsRooms, transformReduxStateToComponentState]
  );

  const [rooms, setRooms] = useState<RoomPassengers[]>(initialRoomsFromRedux);
  const [collapsedStates, setCollapsedStates] = useState<boolean[]>(() =>
    initialRoomsFromRedux.map((_, index) => index !== 0)
  );
  const [
    maxTravelersError,
    setMaxTravelersError,
  ] = useState<MaxTravelersErrorId | null>(null);

  const [
    needMoreAdultsError,
    setNeedMoreAdultsError,
  ] = useState<NeedMoreAdultsErrorId | null>(null);

  const [roomError, setRoomError] = useState<number | null>(null);

  const handleAddPassenger = (roomIndex: number, travelerCode: string) => {
    const currentTotalTravelers = calculateTotalTravelers(rooms);
    if (currentTotalTravelers >= 9) {
      setMaxTravelersError("PropertiesSelect.maxPassengersError");
      return;
    }

    setRooms((currentRooms) => {
      if (calculateTotalTravelers(currentRooms) >= 9) {
        setMaxTravelersError("PropertiesSelect.maxPassengersError");
        return currentRooms;
      }

      const updatedRooms = [...currentRooms];
      const currentRoom = { ...updatedRooms[roomIndex] };
      const currentValue = (currentRoom[travelerCode] as number) || 0;
      const newValue = currentValue + 1;

      updatedRooms[roomIndex] = {
        ...currentRoom,
        [travelerCode]: newValue,
        ...(travelerCode === "CHD" && {
          childAges: [
            ...(currentRoom.childAges || []),
            ...(newValue > currentValue ? [10] : []),
          ].slice(0, newValue),
        }),
      };
      setMaxTravelersError(null);
      return updatedRooms;
    });
  };

  const handleRemovePassenger = (roomIndex: number, travelerCode: string) => {
    setRooms((currentRooms) => {
      const updatedRooms = [...currentRooms];
      const currentRoom = { ...updatedRooms[roomIndex] };
      const currentValue = (currentRoom[travelerCode] as number) || 0;
      const newValue = currentValue - 1;

      const minValue = travelerCode === "ADT" ? 1 : 0;
      if (newValue < minValue) {
        return currentRooms;
      }

      updatedRooms[roomIndex] = {
        ...currentRoom,
        [travelerCode]: newValue,
        ...(travelerCode === "CHD" && {
          childAges: (currentRoom.childAges || []).slice(0, newValue),
        }),
      };

      const newTotalTravelers = calculateTotalTravelers(updatedRooms);
      if (newTotalTravelers < 9 && maxTravelersError) {
        setMaxTravelersError(null);
      }

      return updatedRooms;
    });
  };

  const handleChildAgeChange = (
    roomIndex: number,
    childIndex: number,
    age: number
  ) => {
    setRooms((currentRooms) => {
      const updatedRooms = [...currentRooms];
      const childAges = [...(updatedRooms[roomIndex].childAges || [])];
      childAges[childIndex] = Number(age);
      updatedRooms[roomIndex] = {
        ...updatedRooms[roomIndex],
        childAges,
      };
      return updatedRooms;
    });
  };

  const resetRoomsToDefault = () => {
    const defaultRoom: RoomPassengers = { ADT: 1, CHD: 0, childAges: [] };
    setRooms([defaultRoom]);
    setCollapsedStates([false]);
    setMaxTravelersError(null);
  };

  const addRoom = () => {
    if (rooms.length < Number(hotelMaxRooms)) {
      const newRoom: RoomPassengers = { ADT: 1, CHD: 0, childAges: [] };
      setRooms((currentRooms) => [...currentRooms, newRoom]);
      setCollapsedStates((prev) => [...prev, true]);
    }
  };

  const removeRoom = (roomIndexToRemove: number) => {
    setRooms((currentRooms) => {
      if (currentRooms.length > 1) {
        const updatedRooms = currentRooms.filter(
          (_, index) => index !== roomIndexToRemove
        );
        setCollapsedStates((prev) =>
          prev.filter((_, index) => index !== roomIndexToRemove)
        );
        const newTotalTravelers = calculateTotalTravelers(updatedRooms);

        if (newTotalTravelers < 9 && maxTravelersError) {
          setMaxTravelersError(null);
        }

        return updatedRooms;
      }

      return currentRooms;
    });
  };

  const travelerTypesSettings =
    hotelSearchSettings?.RoomGuestLimits?.GuestLimit;

  const childSettings = travelerTypesSettings.find(
    (limit) => limit.Code === "CHD"
  );

  const toggleRoomCollapse = (indexToToggle: number) => {
    setCollapsedStates((prevStates) => {
      const isOpening = prevStates[indexToToggle];
      return prevStates.map((state, i) => {
        if (i === indexToToggle) {
          return !state;
        }
        if (isOpening) {
          return true;
        }
        return state;
      });
    });
  };

  useEffect(() => {
    const currentReduxStructure = {
      rooms: rooms.reduce((acc, room, index) => {
        acc[index] = {
          ADT: room.ADT,
          CHD: room.CHD,
          childAges: room.childAges || [],
        };
        return acc;
      }, {} as typeof searchHotelsRooms),
    };

    dispatch(setHotelPassengers(currentReduxStructure));

    rooms.some((room, index) => {
      if (childSettings?.LessOrEqualToAdults !== "true") return false;
      const childrenOlderThanMaxAge =
        (room.childAges as number[] | undefined)?.filter((age) => {
          return Number(age) <= Number(childSettings?.MaxAge);
        }).length || 0;

      if (room.ADT < childrenOlderThanMaxAge) {
        setNeedMoreAdultsError("PropertiesSelect.needMoreAdultsThanChildren");
        setErrorMessage("PropertiesSelect.needMoreAdultsThanChildren");
        setRoomError(index);
        return true;
      }
      setRoomError(null);
      setErrorMessage(null);
      return setNeedMoreAdultsError(null);
    });

    const travelerCount = calculateTotalTravelers(rooms);
    if (travelerCount >= 9) {
      setMaxTravelersError("PropertiesSelect.maxPassengersError");
    } else if (travelerCount < 9 && maxTravelersError) {
      setMaxTravelersError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    rooms,
    maxTravelersError,
    childSettings?.LessOrEqualToAdults,
    childSettings?.MaxAge,
  ]);

  return {
    rooms,
    collapsedStates,
    maxTravelersError,
    needMoreAdultsError,
    roomError,
    handleAddPassenger,
    handleRemovePassenger,
    handleChildAgeChange,
    resetRoomsToDefault,
    addRoom,
    removeRoom,
    toggleRoomCollapse,
    isDefaultState:
      rooms.length === 1 &&
      rooms[0].ADT === 1 &&
      rooms[0].CHD === 0 &&
      (!rooms[0].childAges || rooms[0].childAges.length === 0),
  };
};
